import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ordersAPI, tasksAPI } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { format } from 'date-fns';
import {
  Package,
  Printer,
  Eye,
  ShoppingCart,
  ArrowLeft,
  Mail,
  MessageSquare,
  Send,
  Edit,
  Save,
  X
} from 'lucide-react';

const OrdersPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [orders, setOrders] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [selectedTasks, setSelectedTasks] = useState(location.state?.selectedTasks || []);
  const [showCreateOrder, setShowCreateOrder] = useState(!!location.state?.selectedTasks);
  const [showOrderDetail, setShowOrderDetail] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  
  // Share dialog
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [shareMethod, setShareMethod] = useState('email');
  const [shareEmail, setShareEmail] = useState('');
  const [sharePhone, setSharePhone] = useState('');

  // Edit mode
  const [isEditing, setIsEditing] = useState(false);
  const [editItems, setEditItems] = useState([]);
  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [ordersRes, tasksRes] = await Promise.all([
        ordersAPI.list(),
        tasksAPI.list({ archived: false })
      ]);
      setOrders(ordersRes.data);
      setTasks(tasksRes.data.filter(t => t.parts?.some(p => !p.ordered && !p.unavailable)));
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateOrder = async () => {
    if (selectedTasks.length === 0) {
      toast.error('Select at least one task');
      return;
    }

    try {
      const response = await ordersAPI.create(selectedTasks);
      toast.success('Order created');
      setShowCreateOrder(false);
      setSelectedTasks([]);
      fetchData();
      // Show the order
      setSelectedOrder(response.data);
      setShowOrderDetail(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create order');
    }
  };

  const openOrderDetail = async (orderId) => {
    try {
      const response = await ordersAPI.get(orderId);
      setSelectedOrder(response.data);
      setShowOrderDetail(true);
      setIsEditing(false);
    } catch (error) {
      toast.error('Failed to load order');
    }
  };

  const startEditing = () => {
    setEditItems((selectedOrder?.items || []).map(item => ({ ...item })));
    setIsEditing(true);
  };

  const cancelEditing = () => {
    setIsEditing(false);
    setEditItems([]);
  };

  const updateEditItem = (idx, field, value) => {
    const items = [...editItems];
    items[idx] = { ...items[idx], [field]: field === 'quantity' ? parseInt(value) || 1 : value };
    setEditItems(items);
  };

  const handleSaveOrder = async () => {
    try {
      const res = await ordersAPI.update(selectedOrder.order_id, { items: editItems });
      setSelectedOrder(res.data);
      setIsEditing(false);
      toast.success('Order updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to save order');
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const groupPartsBySupplier = (items) => {
    const grouped = {};
    items.forEach(item => {
      const supplier = item.supplier || 'Unspecified';
      if (!grouped[supplier]) {
        grouped[supplier] = [];
      }
      grouped[supplier].push(item);
    });
    return grouped;
  };

  const toggleTaskSelection = (taskId) => {
    setSelectedTasks(prev => 
      prev.includes(taskId) 
        ? prev.filter(id => id !== taskId)
        : [...prev, taskId]
    );
  };

  // Generate parts list text for sharing
  const generatePartsListText = () => {
    if (!selectedOrder) return '';
    
    const grouped = groupPartsBySupplier(selectedOrder.items || []);
    let text = `Parts Order - ${selectedOrder.order_id}\n`;
    text += `Date: ${format(new Date(selectedOrder.created_at), 'PPP')}\n\n`;
    
    Object.entries(grouped).forEach(([supplier, items]) => {
      text += `--- ${supplier} ---\n`;
      items.forEach(item => {
        text += `• ${item.part_name} (Qty: ${item.quantity})\n`;
      });
      text += '\n';
    });
    
    return text;
  };

  // Handle share via email or SMS
  const handleShare = () => {
    const partsText = generatePartsListText();
    
    if (shareMethod === 'email') {
      if (!shareEmail) {
        toast.error('Please enter an email address');
        return;
      }
      const subject = `Parts Order - ${selectedOrder?.order_id}`;
      const mailtoUrl = `mailto:${shareEmail}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(partsText)}`;
      window.open(mailtoUrl);
      toast.success('Email client opened');
      setShowShareDialog(false);
    } else {
      if (!sharePhone) {
        toast.error('Please enter a phone number');
        return;
      }
      // SMS link (works on mobile devices)
      const smsUrl = `sms:${sharePhone}?body=${encodeURIComponent(partsText)}`;
      window.open(smsUrl);
      toast.success('SMS app opened');
      setShowShareDialog(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="orders-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-heading">Parts Orders</h1>
          <p className="text-slate-600">Manage parts orders from tasks</p>
        </div>
        <Button onClick={() => setShowCreateOrder(true)} data-testid="new-order-btn">
          <ShoppingCart className="mr-2 h-4 w-4" />
          New Order
        </Button>
      </div>

      {/* Orders History */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading">Order History</CardTitle>
        </CardHeader>
        <CardContent>
          {orders.length === 0 ? (
            <div className="text-center py-12">
              <Package className="h-12 w-12 mx-auto mb-4 text-slate-300" />
              <p className="text-slate-600">No orders yet</p>
              <Button 
                variant="outline" 
                className="mt-4"
                onClick={() => setShowCreateOrder(true)}
              >
                Create your first order
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Order ID</TableHead>
                  <TableHead>Items</TableHead>
                  <TableHead>Tasks</TableHead>
                  <TableHead>Suggested Suppliers</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {orders.map((order) => {
                  const taskTitles = [...new Set(order.items?.map(i => i.task_title).filter(Boolean))];
                  const suppliers = [...new Set(order.items?.map(i => i.supplier).filter(s => s && s !== 'Unspecified'))];
                  return (
                    <TableRow key={order.order_id} className="cursor-pointer hover:bg-slate-50">
                      <TableCell className="font-medium">{order.order_id}</TableCell>
                      <TableCell>
                        <Badge variant="secondary">{order.items?.length || 0} parts</Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1 max-w-xs">
                          {taskTitles.map((t, i) => (
                            <Badge key={i} variant="outline" className="text-xs">{t}</Badge>
                          ))}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1 max-w-xs">
                          {suppliers.length > 0 ? suppliers.map((s, i) => (
                            <Badge key={i} className="text-xs bg-slate-100 text-slate-700">{s}</Badge>
                          )) : <span className="text-slate-400 text-sm">—</span>}
                        </div>
                      </TableCell>
                      <TableCell>
                        {format(new Date(order.created_at), 'PPP')}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => openOrderDetail(order.order_id)}
                        >
                          <Eye className="mr-2 h-4 w-4" />
                          View
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create Order Dialog */}
      <Dialog open={showCreateOrder} onOpenChange={setShowCreateOrder}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="font-heading">Create Parts Order</DialogTitle>
            <DialogDescription>
              Select tasks to include their unordered parts in a new order.
            </DialogDescription>
          </DialogHeader>
          
          {tasks.length === 0 ? (
            <div className="text-center py-8">
              <Package className="h-12 w-12 mx-auto mb-4 text-slate-300" />
              <p className="text-slate-600">No tasks with unordered parts</p>
              <Button variant="outline" className="mt-4" onClick={() => navigate('/dashboard/tasks')}>
                <ArrowLeft className="mr-2 h-4 w-4" />
                Go to Tasks
              </Button>
            </div>
          ) : (
            <>
              <div className="max-h-96 overflow-auto space-y-2">
                {tasks.map((task) => {
                  const unorderedParts = task.parts?.filter(p => !p.ordered && !p.unavailable) || [];
                  return (
                    <div 
                      key={task.task_id}
                      className={`p-4 rounded-lg border ${selectedTasks.includes(task.task_id) ? 'border-indigo-500 bg-indigo-50' : ''}`}
                    >
                      <div className="flex items-start gap-3">
                        <Checkbox
                          checked={selectedTasks.includes(task.task_id)}
                          onCheckedChange={() => toggleTaskSelection(task.task_id)}
                        />
                        <div className="flex-1">
                          <h4 className="font-medium">{task.title}</h4>
                          <div className="mt-2 space-y-1">
                            {unorderedParts.map(part => (
                              <div key={part.part_id} className="text-sm text-slate-600 flex items-center gap-2">
                                <span className="w-4 h-4 bg-slate-200 rounded-full flex items-center justify-center text-xs">
                                  {part.quantity}
                                </span>
                                <span>{part.name}</span>
                                {part.supplier && (
                                  <Badge variant="outline" className="text-xs">{part.supplier}</Badge>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                        <Badge variant="secondary">{unorderedParts.length} parts</Badge>
                      </div>
                    </div>
                  );
                })}
              </div>
              
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowCreateOrder(false)}>
                  Cancel
                </Button>
                <Button onClick={handleCreateOrder} disabled={selectedTasks.length === 0}>
                  Create Order ({selectedTasks.length} tasks)
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Order Detail Dialog - Print Friendly */}
      <Dialog open={showOrderDetail} onOpenChange={setShowOrderDetail}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-auto print:max-w-none print:max-h-none print:overflow-visible print:shadow-none">
          {selectedOrder && (
            <>
              <DialogHeader className="print:hidden">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <DialogTitle className="font-heading">Order: {selectedOrder.order_id}</DialogTitle>
                  <div className="flex gap-2">
                    {isEditing ? (
                      <>
                        <Button variant="outline" size="sm" onClick={cancelEditing} data-testid="cancel-edit-order-btn">
                          <X className="mr-2 h-4 w-4" />
                          Cancel
                        </Button>
                        <Button size="sm" onClick={handleSaveOrder} data-testid="save-order-btn">
                          <Save className="mr-2 h-4 w-4" />
                          Save
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button variant="outline" size="sm" onClick={startEditing} data-testid="edit-order-btn">
                          <Edit className="mr-2 h-4 w-4" />
                          Edit
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => setShowShareDialog(true)}>
                          <Send className="mr-2 h-4 w-4" />
                          Share
                        </Button>
                        <Button variant="outline" size="sm" onClick={handlePrint}>
                          <Printer className="mr-2 h-4 w-4" />
                          Print
                        </Button>
                      </>
                    )}
                  </div>
                </div>
                <DialogDescription>
                  Created on {format(new Date(selectedOrder.created_at), 'PPP')}
                </DialogDescription>
              </DialogHeader>

              {/* Print Header */}
              <div className="hidden print:block mb-6 pb-4 border-b-2 border-slate-900">
                <div className="flex justify-between items-start">
                  <div>
                    <h1 className="text-3xl font-bold">Parts Order</h1>
                    <p className="text-lg text-slate-600 mt-1">Order ID: {selectedOrder.order_id}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-medium">TaskMaster</p>
                    <p className="text-slate-600">{format(new Date(selectedOrder.created_at), 'PPP')}</p>
                  </div>
                </div>
              </div>

              <div className="space-y-6">
                {isEditing ? (
                  /* ===== EDIT MODE: Flat editable table ===== */
                  <div className="border rounded-lg overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-12">#</TableHead>
                          <TableHead>Part Name</TableHead>
                          <TableHead>Task</TableHead>
                          <TableHead>Supplier</TableHead>
                          <TableHead className="text-center w-24">Qty</TableHead>
                          <TableHead className="text-right w-28">Unit $</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {editItems.map((item, idx) => (
                          <TableRow key={idx} data-testid={`edit-item-${idx}`}>
                            <TableCell className="font-medium text-slate-400">{idx + 1}</TableCell>
                            <TableCell>
                              <span className="text-sm">{item.part_name}</span>
                            </TableCell>
                            <TableCell className="text-slate-600 text-sm">{item.task_title}</TableCell>
                            <TableCell>
                              <Input
                                value={item.supplier || ''}
                                onChange={(e) => updateEditItem(idx, 'supplier', e.target.value)}
                                className="h-8 text-sm"
                                placeholder="Supplier name"
                                data-testid={`edit-supplier-${idx}`}
                              />
                            </TableCell>
                            <TableCell className="text-center">
                              <Input
                                type="number"
                                min="1"
                                value={item.quantity}
                                onChange={(e) => updateEditItem(idx, 'quantity', e.target.value)}
                                className="h-8 text-sm w-16 mx-auto text-center"
                                data-testid={`edit-quantity-${idx}`}
                              />
                            </TableCell>
                            <TableCell className="text-right">
                              <Input
                                type="number"
                                min="0"
                                step="0.01"
                                value={item.unit_price ?? ''}
                                onChange={(e) => updateEditItem(idx, 'unit_price', e.target.value === '' ? null : parseFloat(e.target.value))}
                                className="h-8 text-sm w-24 ml-auto text-right"
                                placeholder="0.00"
                                data-testid={`edit-unit-price-${idx}`}
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                ) : (
                  /* ===== VIEW MODE: Grouped by supplier ===== */
                  Object.entries(groupPartsBySupplier(selectedOrder.items || [])).map(([supplier, items]) => {
                    const supplierTotal = items.reduce((sum, i) => sum + ((i.unit_price || 0) * (i.quantity || 0)), 0);
                    return (
                  <div key={supplier} className="border rounded-lg overflow-hidden print:break-inside-avoid">
                    <div className="bg-slate-100 px-4 py-3 print:bg-slate-200 flex items-center justify-between">
                      <div>
                        <h3 className="font-bold text-lg">{supplier}</h3>
                        <p className="text-sm text-slate-600">{items.length} item(s)</p>
                      </div>
                      {supplierTotal > 0 && (
                        <span className="font-bold text-lg">${supplierTotal.toFixed(2)}</span>
                      )}
                    </div>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-12 print:hidden">#</TableHead>
                          <TableHead>Part Name</TableHead>
                          <TableHead>Task</TableHead>
                          <TableHead className="text-center w-20">Qty</TableHead>
                          <TableHead className="text-right w-24">Unit $</TableHead>
                          <TableHead className="text-right w-24">Total</TableHead>
                          <TableHead className="text-center w-20 print:hidden">Shop</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {items.map((item, idx) => {
                          const lineTotal = (item.unit_price || 0) * (item.quantity || 0);
                          return (
                          <TableRow key={idx} className="print:border-b">
                            <TableCell className="font-medium print:hidden">{idx + 1}</TableCell>
                            <TableCell className="font-medium">{item.part_name}</TableCell>
                            <TableCell className="text-slate-600 text-sm">{item.task_title}</TableCell>
                            <TableCell className="text-center font-bold text-lg">{item.quantity}</TableCell>
                            <TableCell className="text-right text-sm">
                              {item.unit_price != null ? `$${Number(item.unit_price).toFixed(2)}` : <span className="text-slate-300">—</span>}
                            </TableCell>
                            <TableCell className="text-right font-medium text-sm">
                              {item.unit_price != null ? `$${lineTotal.toFixed(2)}` : <span className="text-slate-300">—</span>}
                            </TableCell>
                            <TableCell className="text-center print:hidden">
                              <a
                                href={`https://www.google.com/search?q=${encodeURIComponent((item.part_name + (item.supplier && item.supplier !== 'Unspecified' ? ' ' + item.supplier : '') + ' buy'))}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-indigo-600 hover:text-indigo-800 hover:underline text-sm"
                              >
                                Search
                              </a>
                            </TableCell>
                          </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                    );
                  })
                )}

                {/* Grand Total */}
                {!isEditing && (() => {
                  const grandTotal = (selectedOrder.items || []).reduce((sum, i) => sum + ((i.unit_price || 0) * (i.quantity || 0)), 0);
                  return grandTotal > 0 ? (
                    <div className="flex justify-end pt-2">
                      <div className="bg-slate-50 border rounded-lg px-6 py-3 text-right">
                        <span className="text-sm text-slate-500 mr-4">Order Total</span>
                        <span className="text-xl font-bold">${grandTotal.toFixed(2)}</span>
                      </div>
                    </div>
                  ) : null;
                })()}
              </div>

              {/* Print Footer */}
              <div className="hidden print:block mt-8 pt-4 border-t text-center text-sm text-slate-500">
                <p>Generated by TaskMaster on {format(new Date(), 'PPP')}</p>
              </div>

              <DialogFooter className="print:hidden">
                <Button variant="outline" onClick={() => setShowOrderDetail(false)}>
                  Close
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Share Dialog */}
      <Dialog open={showShareDialog} onOpenChange={setShowShareDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading">Share Parts List</DialogTitle>
            <DialogDescription>
              Send the parts list via email or SMS
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="flex gap-2">
              <Button
                variant={shareMethod === 'email' ? 'default' : 'outline'}
                onClick={() => setShareMethod('email')}
                className="flex-1"
              >
                <Mail className="mr-2 h-4 w-4" />
                Email
              </Button>
              <Button
                variant={shareMethod === 'sms' ? 'default' : 'outline'}
                onClick={() => setShareMethod('sms')}
                className="flex-1"
              >
                <MessageSquare className="mr-2 h-4 w-4" />
                SMS
              </Button>
            </div>
            
            {shareMethod === 'email' ? (
              <div>
                <Label>Email Address</Label>
                <Input
                  type="email"
                  value={shareEmail}
                  onChange={(e) => setShareEmail(e.target.value)}
                  placeholder="email@example.com"
                  className="mt-2"
                />
              </div>
            ) : (
              <div>
                <Label>Phone Number</Label>
                <Input
                  type="tel"
                  value={sharePhone}
                  onChange={(e) => setSharePhone(e.target.value)}
                  placeholder="+1 234 567 8900"
                  className="mt-2"
                />
              </div>
            )}

            <div className="bg-slate-50 p-3 rounded-lg">
              <p className="text-xs text-slate-500 mb-2">Preview:</p>
              <pre className="text-xs whitespace-pre-wrap max-h-32 overflow-auto">
                {generatePartsListText().slice(0, 300)}...
              </pre>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowShareDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleShare}>
              <Send className="mr-2 h-4 w-4" />
              Send
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Print Styles */}
      <style>{`
        @media print {
          body * {
            visibility: hidden;
          }
          [role="dialog"] * {
            visibility: visible;
          }
          [role="dialog"] {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
            max-width: 100% !important;
            height: auto;
            max-height: none !important;
            overflow: visible !important;
            background: white;
            box-shadow: none !important;
            border: none !important;
          }
          .print\\:hidden {
            display: none !important;
          }
          .print\\:block {
            display: block !important;
          }
          .print\\:inline {
            display: inline !important;
          }
          .print\\:break-inside-avoid {
            break-inside: avoid;
          }
          table {
            width: 100%;
          }
          th, td {
            padding: 8px 12px;
            border-bottom: 1px solid #e2e8f0;
          }
        }
      `}</style>
    </div>
  );
};

export default OrdersPage;
